import { useState, useRef, useEffect, type ReactNode } from 'react'
import { Chevron } from '../ui/Chevron'

export interface HeaderUser {
  displayName: string
  email: string
  photoURL?: string
  /**
   * Ordered avatar images, tried in turn; a candidate that fails to LOAD falls
   * through to the next, and after the last come the initials. When given it
   * replaces `photoURL`. The Control Center passes logo → favicon → the user's
   * photo (CC review #5, 2026-09-22).
   */
  avatarUrls?: string[]
  /** Initials source when it should not be the display name (e.g. the business). */
  initialsFrom?: string
}

export interface HeaderProps {
  title: string
  user: HeaderUser
  onDarkModeToggle: () => void
  isDarkMode: boolean
  notificationCount?: number
  /** Called when the hamburger button is clicked (mobile only) */
  onMenuToggle?: () => void
  /** Optional slot rendered after the title for location switcher or other controls */
  titleSlot?: ReactNode
  /**
   * Optional brand logo. When provided, the header renders the logo + a divider
   * + the title as a small uppercase label (matching the prototype). When
   * omitted, the title renders as a plain large heading (legacy behavior).
   */
  logoSrc?: string
  /** Logo shown in dark mode (falls back to logoSrc). */
  logoDarkSrc?: string
  /** Optional center slot — admin uses this for the live date/time clock. */
  centerSlot?: ReactNode
  /** Optional status badge rendered before the right-hand controls (e.g. "Rudy v1.0"). */
  statusBadge?: ReactNode
  /** Dropdown menu handlers. Falls back to no-op / marketing redirect when omitted. */
  onProfileClick?: () => void
  onNotificationsClick?: () => void
  onSettingsClick?: () => void
  onSignOut?: () => void
}

/**
 * Dashboard header — matches the prototype's top bar:
 * - Page title on the left (with optional slot for location switcher)
 * - Dark mode toggle, notifications, and user menu on the right
 * - Hamburger button on mobile (calls onMenuToggle)
 */
export function Header({
  title,
  user,
  onDarkModeToggle,
  isDarkMode,
  notificationCount = 0,
  onMenuToggle,
  titleSlot,
  logoSrc,
  logoDarkSrc,
  centerSlot,
  statusBadge,
  onProfileClick,
  onNotificationsClick,
  onSettingsClick,
  onSignOut,
}: HeaderProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setUserMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const candidates = (user.avatarUrls ?? (user.photoURL ? [user.photoURL] : [])).filter(Boolean)
  // Index of the first candidate that has not failed to load; past the end = initials.
  const [avatarIdx, setAvatarIdx] = useState(0)
  // The client doc can arrive after the first render — restart the chain when the
  // candidate list changes, or an early miss would skip the logo that just loaded.
  const candidateKey = candidates.join('|')
  useEffect(() => { setAvatarIdx(0) }, [candidateKey])
  const avatarSrc = candidates[avatarIdx]
  const initials = (user.initialsFrom || user.displayName)
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <header className="theme-header border-b theme-border h-16 flex items-center gap-4 px-4 md:px-6 flex-shrink-0 sticky top-0 z-10">
      {/* Mobile hamburger */}
      {onMenuToggle && (
        <button
          onClick={onMenuToggle}
          className="btn-icon md:hidden"
          aria-label="Open navigation menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Brand logo + page title (or plain title when no logo provided) */}
      <div className="flex items-center gap-3 min-w-0">
        {logoSrc ? (
          <>
            <img
              src={isDarkMode ? (logoDarkSrc ?? logoSrc) : logoSrc}
              alt="Best AI Solutions"
              className="h-8 w-auto flex-shrink-0"
            />
            <div
              className="theme-text-secondary text-xs tracking-widest uppercase border-l pl-3 hidden sm:block truncate"
              style={{ borderColor: 'var(--border)' }}
            >
              {title}
            </div>
          </>
        ) : (
          <h1 className="text-lg font-semibold theme-text truncate">{title}</h1>
        )}
        {titleSlot}
      </div>

      {/* Center slot (admin live clock) — also acts as the flex spacer that
          pushes the right-hand controls to the edge. */}
      {centerSlot ? (
        <div className="flex-1 text-center min-w-0">
          <div className="hidden md:block">{centerSlot}</div>
        </div>
      ) : (
        <div className="flex-1" />
      )}

      {/* Right controls */}
      <div className="flex items-center gap-2">
        {/* Status badge (e.g. "Rudy v1.0") */}
        {statusBadge && <div className="hidden sm:flex items-center mr-1">{statusBadge}</div>}

        {/* Dark mode toggle */}
        <button
          onClick={onDarkModeToggle}
          className="btn-icon relative"
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? (
            // Sun icon (switch to light)
            <svg className="w-5 h-5 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            // Moon icon (switch to dark)
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        {/* Notifications */}
        <button
          onClick={onNotificationsClick}
          className="btn-icon relative"
          aria-label={`Notifications${notificationCount > 0 ? ` (${notificationCount} unread)` : ''}`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          {notificationCount > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
        </button>

        {/* User avatar + name + dropdown */}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-slate-700/30 transition-colors"
            aria-label={`User menu for ${user.displayName}`}
          >
            {avatarSrc ? (
              <img
                key={avatarSrc}
                src={avatarSrc}
                alt={user.displayName}
                // A logo is a mark, not a face: contain it on white so it is not cropped.
                className="w-7 h-7 rounded-full object-contain bg-white"
                onError={() => setAvatarIdx((i) => i + 1)}
                // ⚠️ A favicon service answers a MISS with a placeholder globe (Google:
                // status 404 but a valid 16×16 image when 64 was asked for, measured
                // 2026-09-22 — so onError never fires). Anything under 24px is a miss or
                // too small to read at this size; fall through to the next candidate.
                onLoad={(e) => { if (e.currentTarget.naturalWidth < 24) setAvatarIdx((i) => i + 1) }}
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center">
                <span className="text-xs font-bold text-white">{initials}</span>
              </div>
            )}
            <span className="hidden sm:block text-sm font-medium theme-text truncate max-w-[120px]">
              {user.displayName}
            </span>
            <Chevron open={userMenuOpen} inline className="hidden sm:inline-block" />
          </button>
          {userMenuOpen && (
            <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-slate-700 rounded-lg shadow-lg border border-slate-200 dark:border-slate-600 py-1 z-50">
              {/* Identity block */}
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-600">
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{user.displayName}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</div>
              </div>
              <button onClick={() => { setUserMenuOpen(false); onProfileClick?.() }} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                Profile &amp; Account
              </button>
              <button onClick={() => { setUserMenuOpen(false); onNotificationsClick?.() }} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>
                Notifications
              </button>
              <button onClick={() => { setUserMenuOpen(false); onSettingsClick?.() }} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                Settings
              </button>
              <div className="border-t border-slate-200 dark:border-slate-600 my-1" />
              <button
                onClick={() => {
                  setUserMenuOpen(false)
                  if (onSignOut) onSignOut()
                  else window.location.href = 'https://bestaisolutions.com/#login'
                }}
                className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
